import { Button, KIND } from 'baseui/button'
import { StyleObject } from 'styletron-standard'
import { lightTheme } from '@/customTheme'
import {
  expandBorder,
  expandRadius,
  expandBox,
  normalizeShorthandStyle,
} from '@/utils/baseWebStyleLonghand'

interface SocialLoginButtonStyle extends StyleObject {
  ':hover'?: { background?: string, border?: string }
}

export interface SocialLoginButtonProps {
  onClick?: () => void
  style?: SocialLoginButtonStyle
  children: React.ReactNode
  isLoading?: boolean
  disabled?: boolean
}

function SocialLoginButton({
  onClick,
  style,
  children,
  isLoading,
  disabled,
}: SocialLoginButtonProps) {
  const sizeCustom = '48px' // LARGE size
  
  // When disabled but not loading, apply 30% opacity
  const isDisabledNotLoading = disabled && !isLoading
  const isInteractive = !disabled && !isLoading

  // Extract hover from style to handle separately, rest goes to baseStyle
  const {
    ':hover': hoverStyle,
    useRoundedCorners: _ignoredUseRoundedCorners,
    ...baseStyle
  } = (style || {}) as SocialLoginButtonStyle & { useRoundedCorners?: unknown }
  const normalizedBaseStyle = normalizeShorthandStyle(baseStyle as StyleObject)
  
  // Base background from style prop or default grayScale50 (#F2F2F2) for Google-like buttons
  const baseBackground = (normalizedBaseStyle?.background as string) || lightTheme.colors.grayScale50
  const hoverBackground = hoverStyle?.background
  const hoverBorder = hoverStyle?.border

  // Get text color for spinner
  const textColor = (normalizedBaseStyle?.color as string) || lightTheme.colors.contentPrimary

  return (
    <Button
      disabled={disabled || isLoading}
      isLoading={isLoading}
      kind={KIND.secondary}
      onClick={onClick ? onClick : null}
      overrides={{
        BaseButton: {
          style: ({ $theme }) => ({
            ...$theme,
            ...lightTheme.typography.Small14Semibold,
            width: '100%',
            height: sizeCustom,
            ...expandRadius('100px'),
            ...expandBox('padding', '0px 16px'),
            outline: 'none',
            ...expandBorder('0', 'none', 'transparent'),
            color: textColor,
            // Spread base styles (without :hover)
            ...normalizedBaseStyle,
            // Set background explicitly - always use baseBackground, override BaseUI's disabled gray
            background: `${baseBackground} !important`,
            // Override opacity and cursor based on state
            // - Disabled (not loading): 0.3 opacity
            // - Loading: 1 opacity (fully visible)
            // - Interactive: 1 opacity
            opacity: isDisabledNotLoading ? 0.3 : 1,
            cursor: isInteractive ? 'pointer' : 'not-allowed',
            // Hover: no changes when disabled OR loading, only apply hover when fully interactive
            ':hover': {
              background: isInteractive && hoverBackground 
                ? `${hoverBackground} !important` 
                : `${baseBackground} !important`,
              ...normalizeShorthandStyle(
                hoverBorder
                  ? ({ border: hoverBorder } as StyleObject)
                  : undefined,
              ),
              opacity: isDisabledNotLoading ? 0.3 : 1,
            },
            // Active: no changes when disabled OR loading, only apply active when fully interactive
            ':active': {
              background: isInteractive && hoverBackground 
                ? `${hoverBackground} !important` 
                : `${baseBackground} !important`,
              opacity: isDisabledNotLoading ? 0.3 : 1,
            },
            // Override BaseUI's disabled state styling (applies to both disabled and loading)
            ':disabled': {
              background: `${baseBackground} !important`,
              // Keep opacity at 0.3 only for disabled (not loading), loading stays at 1
              opacity: isDisabledNotLoading ? 0.3 : 1,
              cursor: 'not-allowed',
              color: textColor,
            },
          }),
        },
        LoadingSpinner: {
          style: {
            borderTopColor: textColor,
            borderRightColor: textColor,
            borderBottomColor: textColor,
            borderLeftColor: 'transparent',
            width: '24px',
            height: '24px',
          },
        },
      }}
    >
      {children}
    </Button>
  )
}

export default SocialLoginButton
